import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Lock, Flame, Award, Clock, TrendingUp, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { tierMeta } from "@/lib/gamification";
import { fmtINR } from "@/lib/financial";
import ClientSidebar from "@/components/ClientSidebar";

function ScoreRing({ score, tier }) {
  const meta = tierMeta(tier.key);
  const pct = Math.min(100, Math.max(0, score));
  const size = 200;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E2DA" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={meta.color} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={off}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="mono text-5xl font-medium text-[#1C1D1C]">{score}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8D8A] mt-1">/ 100</div>
      </div>
    </div>
  );
}

function BadgeCard({ badge }) {
  const unlocked = badge.unlocked;
  return (
    <div
      data-testid={`badge-${badge.key}`}
      className={`rounded-2xl border p-5 transition-all duration-300 ${unlocked
        ? "border-[#C78D4A]/40 bg-gradient-to-br from-[#F7ECD8]/60 to-white hover:-translate-y-0.5 hover:shadow-lg"
        : "border-[#E5E2DA] bg-[#FDFBF7] opacity-70"}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`h-12 w-12 rounded-full flex items-center justify-center ${unlocked ? "bg-[#C78D4A] text-white" : "bg-[#E5E2DA] text-[#8A8D8A]"}`}>
          {unlocked ? <Award className="h-6 w-6" /> : <Lock className="h-5 w-5" />}
        </div>
        {unlocked && <CheckCircle2 className="h-5 w-5 text-[#3A5C45]" />}
      </div>
      <div className="font-heading text-lg text-[#1C1D1C]">{badge.label}</div>
      <div className="text-sm text-[#5B5E5B] mt-1.5 leading-relaxed">{badge.hint}</div>
      <div className={`mt-3 text-[10px] uppercase tracking-[0.2em] font-bold ${unlocked ? "text-[#C97B2A]" : "text-[#8A8D8A]"}`}>
        {unlocked ? "Unlocked" : "Locked"}
      </div>
    </div>
  );
}

function TimelineDot({ type }) {
  const bg = {
    signup: "#8B9D8B",
    intake: "#1A3626",
    milestone: "#C78D4A",
    networth: "#5C6E7A",
    achievement: "#C97B2A",
  }[type] || "#8A8D8A";
  return <div className="h-3 w-3 rounded-full" style={{ background: bg }} />;
}

function fmtDate(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch { return v; }
}

export default function Achievements() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    let mounted = true;
    (async () => {
      try {
        const { data: g } = await api.get("/gamification");
        if (mounted) setData(g);
      } catch (err) {
        console.error("gamification fetch failed", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user, authLoading, navigate]);

  if (authLoading || loading || !data) {
    return <div className="min-h-screen flex items-center justify-center text-[#5B5E5B]">Loading…</div>;
  }

  const meta = tierMeta(data.tier.key);
  const unlocked = data.achievements.filter((a) => a.unlocked);
  const locked = data.achievements.filter((a) => !a.unlocked);

  return (
    <div className="min-h-screen page-tinted-soft lg:flex">
      <ClientSidebar user={user} onLogout={async () => { await logout(); navigate("/"); }} />
      <div className="flex-1">
        <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="mb-10 fade-up">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Your progress</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3">Achievements & Journey</h1>
            <p className="text-[#5B5E5B] mt-3 max-w-2xl">Every badge here maps to a real move that strengthens your financial future.</p>
          </div>

          {/* Score + Tier hero */}
          <div className="rounded-3xl border border-[#E5E2DA] bg-white p-8 md:p-10 mb-10 grid md:grid-cols-2 gap-10 items-center">
            <div className="flex justify-center md:justify-start">
              <ScoreRing score={data.score} tier={data.tier} />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: meta.color }}>Wealth tier</div>
              <div className="font-heading text-4xl mt-2" style={{ color: meta.color }} data-testid="tier-label">{meta.label}</div>
              {data.tier.next ? (
                <p className="text-[#5B5E5B] mt-3">
                  <span className="mono">{data.tier.points_to_next}</span> more point{data.tier.points_to_next === 1 ? "" : "s"} to reach <span className="font-medium">{data.tier.next.label}</span>.
                </p>
              ) : (
                <p className="text-[#5B5E5B] mt-3">You&apos;ve reached the highest tier — keep the discipline going.</p>
              )}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#FDFBF7] border border-[#E5E2DA] p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Badges</div>
                  <div className="mono text-2xl mt-1" data-testid="badges-count">{data.unlocked_count} / {data.total_badges}</div>
                </div>
                <div className="rounded-xl bg-[#FDFBF7] border border-[#E5E2DA] p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Streak</div>
                  <div className="mono text-2xl mt-1 flex items-center gap-1.5" data-testid="streak-count">
                    {data.streak_months}<Flame className="h-5 w-5 text-[#C97B2A]" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Score breakdown */}
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-[#C97B2A]" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">How your score is built</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.score_breakdown.map((b) => {
                const pct = (b.earned / b.max) * 100;
                return (
                  <div key={b.label} className="rounded-xl bg-[#FDFBF7] border border-[#E5E2DA] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-[#1C1D1C]">{b.label}</div>
                      <div className="mono text-sm text-[#5B5E5B]">{b.earned}/{b.max}</div>
                    </div>
                    <div className="h-1.5 bg-[#E5E2DA] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: pct === 100 ? "#3A5C45" : pct >= 50 ? "#C78D4A" : "#9E473D" }} />
                    </div>
                    <div className="text-xs text-[#8A8D8A] mt-2">{b.hint}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Badges grid */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="h-5 w-5 text-[#C97B2A]" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Badges</div>
            </div>
            {unlocked.length > 0 && (
              <>
                <div className="text-sm text-[#5B5E5B] mb-3">Unlocked</div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  {unlocked.map((b) => <BadgeCard key={b.key} badge={b} />)}
                </div>
              </>
            )}
            {locked.length > 0 && (
              <>
                <div className="text-sm text-[#5B5E5B] mb-3">To unlock</div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {locked.map((b) => <BadgeCard key={b.key} badge={b} />)}
                </div>
              </>
            )}
          </div>

          {/* Wealth Journey Timeline */}
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="flex items-center gap-2 mb-6">
              <Clock className="h-5 w-5 text-[#1A3626]" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Wealth journey</div>
            </div>
            {(data.timeline || []).length === 0 ? (
              <div className="text-sm text-[#8A8D8A] py-6 text-center">Your journey will appear as you take action.</div>
            ) : (
              <ol className="relative border-l-2 border-[#E5E2DA] ml-2 space-y-6" data-testid="wealth-timeline">
                {data.timeline.map((e, i) => (
                  <li key={`${e.type}-${e.at}-${i}`} className="pl-6 relative">
                    <span className="absolute -left-[9px] top-1.5"><TimelineDot type={e.type} /></span>
                    <div className="text-xs text-[#8A8D8A]">{fmtDate(e.at)}</div>
                    <div className="text-[#1C1D1C] font-medium mt-0.5">{e.title}</div>
                    <div className="text-sm text-[#5B5E5B] mt-0.5">{e.detail}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Goals progress */}
          {(data.goals_progress || []).length > 0 && (
            <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7 mt-10">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-[#1A3626]" />
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Goal progress</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {data.goals_progress.map((g, i) => (
                  <div key={`${g.name}-${i}`} className="rounded-xl bg-[#FDFBF7] border border-[#E5E2DA] p-5">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{g.name}</div>
                      <div className="mono text-sm text-[#1A3626]">{g.progress_pct.toFixed(0)}%</div>
                    </div>
                    <div className="mt-3 h-2 bg-[#E5E2DA] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700 bg-[#1A3626]" style={{ width: `${Math.min(100, g.progress_pct)}%` }} />
                    </div>
                    <div className="text-xs text-[#8A8D8A] mt-3">
                      Target ₹ {fmtINR(g.future_cost)} by {g.target_year} · {g.years_left} yrs left
                    </div>
                    <div className="text-xs text-[#8A8D8A]">
                      Projected corpus at current pace: ₹ {fmtINR(g.projected_corpus)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
